import React, { useRef, useEffect } from 'react';
import { isValidScreenId, SCREEN_VERSIONS } from '../../utils/screenIds';

const isDev = process.env.NODE_ENV !== 'production';

const PageIdentifier = ({ pageId, screenId, pageName, screenName, isModal = false, currentModule }) => {
  // Accept both pageId/screenId and pageName/screenName (legacy prop names)
  const resolvedId = pageId || screenId;
  const resolvedName = pageName || screenName;

  // If currentModule is provided, look up page info dynamically
  let finalId = resolvedId;
  let finalName = resolvedName;
  if (currentModule && !isModal) {
    const pageInfo = getPageInfo(currentModule);
    finalId = finalId || pageInfo.id;
    finalName = finalName || pageInfo.name;
  }

  // ── Warn only once per mount (not on every render), dev-only ──────────────
  const warned = useRef(false);
  useEffect(() => {
    if (!isDev || warned.current) return;
    warned.current = true;

    if (!finalId) {
      console.warn('[PageIdentifier] No screen ID provided. Pass pageId or screenId prop.', { currentModule, pageName: finalName });
    } else if (!isValidScreenId(finalId)) {
      console.warn(`[PageIdentifier] Non-standard screen ID format: "${finalId}". Expected pattern: XX-000 or XX-000M1`, { component: finalName });
    }
  }, [finalId, finalName, currentModule]);

  // Graceful fallback for missing ID
  const displayId = finalId || (isDev ? '⚠ MISSING-ID' : '—');
  const version = finalId ? (SCREEN_VERSIONS[finalId] || '') : '';

  if (isModal) {
    return (
      <div className="absolute bottom-2 left-2 z-[9999] bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-mono">
        Screen ID: {displayId}{version ? ` · ${version}` : ''}
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-50 border-t border-gray-200 py-1 px-4">
      <div className="flex justify-between items-center text-xs text-gray-600">
        <span className={`font-mono ${!finalId && isDev ? 'text-red-500 font-semibold' : ''}`}>
          Screen ID: {displayId}{version ? ` · ${version}` : ''}
        </span>
        <span>© 2026 Jewelry Inventory Manager</span>
      </div>
    </div>
  );
};

// Helper function to map currentModule to page info (updated with new screen IDs)
const getPageInfo = (module) => {
  const pageMap = {
    'dashboard': { id: 'DASH-001', name: 'Dashboard' },
    'view-inventory': { id: 'INV-001', name: 'View Inventory' },
    'add-inventory': { id: 'INV-002', name: 'Add Inventory Item' },
    'edit-inventory': { id: 'INV-003', name: 'Edit Inventory Item' },
    'edit-item-details': { id: 'INV-003', name: 'Edit Item Details' },
    'item-details': { id: 'INV-001M1', name: 'Item Details' },
    'material-management': { id: 'MAT-001', name: 'Material Management' },
    'category-management': { id: 'CAT-001', name: 'Category Management' },
    'vendor-management': { id: 'VEN-001', name: 'Vendor Management' },
    'user-management': { id: 'ADM-001', name: 'User Management' },
    'permissions-management': { id: 'ADM-002', name: 'Permissions Management' },
    'upload-profiles': { id: 'ADM-006', name: 'Upload Profiles' },
    'vendor-ledger': { id: 'VEN-005', name: 'Vendor Ledger' },
    'accounting-summary': { id: 'VEN-006', name: 'Accounting Summary' },
    'upload-inventory': { id: 'INV-004', name: 'Upload Inventory' },
    'indian-billing': { id: 'BILL-001', name: 'INR Billing' },
    'us-billing': { id: 'BILL-002', name: 'USD Billing' },
    'estimate-cart': { id: 'BILL-006', name: 'Multi-Item Billing' },
    'billing-history': { id: 'BILL-007', name: 'Billing History' },
    'jewelry-returns': { id: 'RET-001', name: 'Customer Returns' },
    'inr-estimate': { id: 'EST-001', name: 'INR Estimate' },
    'usd-estimate': { id: 'EST-002', name: 'USD Estimate' },
    'view-estimates': { id: 'EST-003', name: 'View Estimates' },
    'available-stock': { id: 'REP-001', name: 'Available Stock Report' },
    'sold-items': { id: 'REP-002', name: 'Sold Items Report' },
    'item-aging': { id: 'REP-007', name: 'Item Aging Report' },
    'vendor-stock': { id: 'REP-004', name: 'Vendor Stock Report' },
    'sales-report': { id: 'REP-006', name: 'Sales by Vendor' },
    'customer-list': { id: 'CUST-001', name: 'Customer List' },
    'gold-rate': { id: 'RATE-001', name: 'Gold Rate Management' },
    'dollar-rate': { id: 'RATE-002', name: 'Dollar Rate Management' },
    'manual-rate-entry': { id: 'RATE-003', name: 'Manual Rate Entry' },
    'exhibition-reconciliation': { id: 'UTL-006', name: 'Exhibition Reconciliation' },
    'barcode-generator': { id: 'UTL-007', name: 'Barcode Generator' },
    'data-sync': { id: 'UTL-005', name: 'Data Sync' },
    'inventory-upload': { id: 'UTL-008', name: 'Inventory Upload' },
    'missing-images': { id: 'UTL-009', name: 'Missing Images' },
    'billing-settings': { id: 'ADM-007', name: 'Billing Settings' },
    'vendor-import': { id: 'ADM-008', name: 'Vendor Import' },
    'cost-breakdown': { id: 'ADM-010', name: 'Cost Breakdown' },
    'catalog-settings': { id: 'ADM-009', name: 'Catalog Settings' },
    'shared-invoice': { id: 'SHR-001', name: 'Shared Invoice View' },
    'business-settings': { id: 'SET-002', name: 'Business Settings' },
    'system-settings': { id: 'ADM-003', name: 'System Settings' },
    'scheme-management': { id: 'SCH-001', name: 'Scheme Management' },
    'scheme-enroll': { id: 'SCH-002', name: 'Enroll Customer' },
    'scheme-payment': { id: 'SCH-003', name: 'Record Payments' },
    'scheme-dashboard': { id: 'SCH-004', name: 'My Schemes' },
    'scheme-redemption': { id: 'SCH-005', name: 'Redemptions' },
    'scheme-reports': { id: 'SCH-006', name: 'Scheme Reports' }
  };

  return pageMap[module] || { id: 'SYS-001', name: 'Unknown Page' };
};

export default PageIdentifier;
